import { ArrowDown, ArrowUp, ArrowUpDown, MessageSquare, MoreVertical, PencilLine, Receipt, Users, Wallet, X } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Menu, type MenuItem } from '@/components/ui/Menu';
import { Pagination } from '@/components/ui/Pagination';
import { QueryError } from '@/components/ui/QueryError';
import { Table, type Column } from '@/components/ui/Table';
import { Tabs, type TabOption } from '@/components/ui/Tabs';
import { useApiQuery } from '@/hooks/useApiQuery';
import { formatCurrency, formatDate } from '@/lib/format';
import { cn, getNameInitials } from '@/lib/utils';
import type {
  FeeAccountListParams,
  FeeAccountRow,
  FeeAccountSort,
  FeeHead,
  FeeStatus,
} from '@/types/fees';
import { feesApi } from '../api/fees.api';
import {
  FEE_HEAD_LABEL,
  FEE_PAGE_SIZES,
  FEE_STATUS_LABEL,
  FEE_STATUS_ORDER,
  FEE_STATUS_TONE,
} from '../constants';

/**
 * The four money figures a row shows, for whichever head is being read.
 *
 * The account is kept per head on the server, so choosing "Tuition fee" is not
 * a different question asked of the database — it is the same rows reported
 * against one of the two columns they already carry. That is why the fee type
 * narrows nothing: an office chasing books money wants the same families with
 * the books numbers, not a shorter list.
 */
function figuresFor(account: FeeAccountRow, head: FeeHead) {
  if (head !== '') {
    const ledger = account[head];
    return {
      payable: ledger.payable,
      concession: ledger.discount,
      collected: ledger.paid,
      balance: ledger.balance,
    };
  }

  return {
    payable: account.payable,
    concession: account.concession,
    collected: account.collected,
    balance: account.balance,
  };
}

/**
 * The line under the collected figure: how far this family has got.
 *
 * Three answers, because there are genuinely three situations and a percentage
 * gets two of them wrong — nothing charged is not 0% paid, and nothing paid
 * against a real bill deserves saying in words rather than as a bar at zero.
 */
function collectedNote(payable: number, collected: number): string {
  if (payable <= 0) return 'Nothing charged';
  if (collected <= 0) return 'Nothing paid yet';

  return `${Math.round((collected / payable) * 100)}%`;
}

/**
 * When this family last paid, as the column prints it.
 *
 * "Never" is wrong for an account carrying money with no receipt behind it —
 * that is the amount taken at admission, before the counter had a day book to
 * write it in — so that case says so rather than contradicting the figure in
 * the column beside it.
 */
function lastPaymentNote(account: FeeAccountRow): string {
  if (account.lastPaidOn) return formatDate(account.lastPaidOn);
  if (Number(account.collected) > 0) return 'At admission';

  return '—';
}

/**
 * A column heading that orders the register with a 3-state toggle:
 * 1st click: High-to-Low (descending)
 * 2nd click: Low-to-High (ascending)
 * 3rd click: Reset to default register order
 */
function SortableHeader({
  label,
  sort,
  currentSort,
  currentDirection,
  onSort,
}: {
  label: string;
  sort: FeeAccountSort;
  currentSort?: FeeAccountSort;
  currentDirection?: 'asc' | 'desc';
  onSort: (patch: { sort?: FeeAccountSort; sortDirection?: 'asc' | 'desc' }) => void;
}) {
  const isActive = currentSort === sort;
  const isAsc = isActive && currentDirection === 'asc';
  const isDesc = isActive && (currentDirection === 'desc' || !currentDirection);

  function handleClick() {
    if (!isActive) {
      // 1st click: High-low (descending)
      onSort({ sort, sortDirection: 'desc' });
    } else if (isDesc) {
      // 2nd click: Low-high (ascending)
      onSort({ sort, sortDirection: 'asc' });
    } else {
      // 3rd click: Reset to default
      onSort({ sort: 'balance', sortDirection: 'desc' });
    }
  }

  const Icon = isActive ? (isAsc ? ArrowUp : ArrowDown) : ArrowUpDown;

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={
        isActive
          ? isDesc
            ? `Sorted by ${label}, high to low. Click to sort low to high.`
            : `Sorted by ${label}, low to high. Click to reset sorting.`
          : `Sort by ${label}, high to low.`
      }
      className={cn(
        'inline-flex cursor-pointer items-center gap-1 rounded-field transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line',
        isActive ? 'text-accent font-bold' : 'hover:text-ink',
      )}
    >
      {label}
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
    </button>
  );
}

export interface FeeRegisterPanelProps {
  /** A college places nobody in a class or a section, so neither is shown. */
  isCollege: boolean;
  canCollect: boolean;
  canManage: boolean;
  canRemind: boolean;
  /**
   * What the register is showing, owned by the page.
   *
   * Lifted out of this panel because the page's own toolbar exports whatever is
   * on screen, and an export that quietly saved a different set of rows from
   * the one being read is worse than no export at all.
   */
  filters: FeeAccountListParams;
  onFiltersChange: (patch: Partial<FeeAccountListParams>) => void;
  /** Which head of the bill the money columns report. */
  head: FeeHead;
  onHeadChange: (head: FeeHead) => void;
  /** Opens one student's account and its receipts. */
  onOpenAccount: (studentUuid: string) => void;
  /**
   * The row actions, which go straight to the form they name rather than to
   * the account dialog — a button reading "Collect" that opened something else
   * is a button that lies. The page loads the account behind them, so only the
   * pressed row's button spins.
   */
  onCollect: (studentUuid: string) => void;
  onSetCharge: (studentUuid: string) => void;
  busyStudentUuid: string | null;
  /** Chases one family. Offered only on a row that actually owes something. */
  onRemind: (account: FeeAccountRow) => void;
  /**
   * Bumped by the page whenever money moves anywhere else on the screen, so the
   * register redraws rather than quoting a balance that has just changed.
   */
  reloadToken: number;
  /** Lifts the session's name up to the page header. */
  onLoaded: (academicYear: { name: string }) => void;
}

/** Every filter "Clear filters" takes off — the Filter by menu's three included. */
const CLEARED_FILTERS: Partial<FeeAccountListParams> = {
  search: '',
  classUuid: '',
  sectionUuid: '',
  status: '',
  studentStatus: '',
  gender: '',
  newThisYear: '',
};

/**
 * The fee register: every student on this session's roll, and where they stand.
 *
 * The one panel that answers the question the module exists for — who has paid,
 * who has paid something, and who has paid nothing. Three ways in, over one
 * table: the filter bar narrows *which* students, the tabs pick a standing, and
 * the fee type chooses which head of the bill the money columns report.
 *
 * The counts on the tabs are the server's, taken over the searched and filtered
 * set but *before* the standing is applied — so picking "Not paid" narrows the
 * table without hollowing out the tabs beside it.
 */
export function FeeRegisterPanel({
  isCollege,
  canCollect,
  canManage,
  canRemind,
  filters,
  onFiltersChange,
  head,
  onHeadChange,
  onOpenAccount,
  onCollect,
  onSetCharge,
  busyStudentUuid,
  onRemind,
  reloadToken,
  onLoaded,
}: FeeRegisterPanelProps) {
  const status = (filters.status ?? '') as FeeStatus | '';

  const { data, isLoading, error, refetch } = useApiQuery(async () => {
    const result = await feesApi.listAccounts(filters);
    onLoaded(result.academicYear);
    return result;
  }, [
    filters.page,
    filters.limit,
    filters.search,
    filters.classUuid,
    filters.sectionUuid,
    filters.status,
    // The Filter by menu's three narrowings. Missing from this list, picking
    // Boys, Girls, New admissions or Graduated changed the filters and never
    // re-read the register.
    filters.studentStatus,
    filters.gender,
    filters.newThisYear,
    filters.sort,
    filters.sortDirection,
    reloadToken,
  ]);

  const hasFilters = Boolean(
    filters.search ||
      filters.classUuid ||
      filters.sectionUuid ||
      status ||
      filters.studentStatus ||
      filters.gender ||
      filters.newThisYear ||
      head,
  );

  /**
   * The standing tabs, each carrying what it is worth in accounts. Every account
   * is in exactly one standing, so "All" is their sum — counted, like them,
   * before a standing is picked, so it never shrinks to the tab on show.
   */
  const allAccounts = data
    ? FEE_STATUS_ORDER.reduce((sum, value) => sum + (data.byStatus[value] ?? 0), 0)
    : undefined;
  const statusTabs: TabOption<FeeStatus | 'all'>[] = [
    { value: 'all', label: 'All accounts', badge: allAccounts?.toLocaleString('en-IN') },
    ...FEE_STATUS_ORDER.map((value) => ({
      value,
      label: FEE_STATUS_LABEL[value],
      badge: data ? (data.byStatus[value] ?? 0).toLocaleString('en-IN') : undefined,
    })),
  ];

  const columns: Column<FeeAccountRow>[] = [
    {
      key: 'student',
      header: 'Student',
      sticky: 'left',
      stickyWidth: 180,
      render: (account) => (
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className="grid h-7.5 w-7.5 shrink-0 place-items-center rounded-full bg-blue-50 dark:bg-blue-950/80 text-[10.5px] font-black text-blue-600 dark:text-blue-400"
            aria-hidden
          >
            {getNameInitials(account.fullName)}
          </span>
          <div className="min-w-0">
            <p className="truncate text-base font-bold text-slate-800 dark:text-slate-100">{account.fullName}</p>
            <p className="truncate font-mono text-sm tabular-nums text-slate-600">
              {account.admissionNumber}
             
            </p>
            <p className=" font-mono text-sm tabular-nums text-slate-600">
              {account.rollNumber ? `  Roll ${account.rollNumber}` : ''}
            </p>
          </div>
        </div>
      ),
    },
    // A college places nobody in a class or a section: the column would be a
    // dash on every row, so it is not drawn at all.
    ...(isCollege
      ? []
      : ([
          {
            key: 'placement',
            header: 'Class & section',
            sticky: 'left',
            stickyWidth: 120,
            render: (account) => (
              <div className="min-w-0 flex flex-row items-center gap-1.5">
                <p className="truncate text-[13px] font-semibold text-slate-800 dark:text-slate-200">{account.className} -</p>
                <p className="truncate text-sm font-medium text-slate-500">{account.sectionName}</p>
              </div>
            ),
          },
        ] satisfies Column<FeeAccountRow>[])),
    {
      key: 'payable',
      header: (
        <SortableHeader
          label="Payable"
          sort="payable"
          currentSort={filters.sort}
          currentDirection={filters.sortDirection}
          onSort={(patch) => onFiltersChange(patch)}
        />
      ),
      align: 'right',
      render: (account) => {
        const figures = figuresFor(account, head);

        return (
          <div>
            <p className=" text-sm font-[650] tabular-nums text-ink">
              {formatCurrency(figures.payable)}
            </p>
            {Number(figures.concession) > 0 && (
              <p className="mt-0.5 font-mono text-2xs tabular-nums text-ink-3">
                − {formatCurrency(figures.concession)}
              </p>
            )}
          </div>
        );
      },
    },
    {
      key: 'collected',
      header: (
        <SortableHeader
          label="Collected"
          sort="collected"
          currentSort={filters.sort}
          currentDirection={filters.sortDirection}
          onSort={(patch) => onFiltersChange(patch)}
        />
      ),
      align: 'right',
      render: (account) => {
        const figures = figuresFor(account, head);
        const payable = Number(figures.payable);
        const collected = Number(figures.collected);
        const share = payable > 0 ? Math.min(100, (collected / payable) * 100) : 0;

        return (
          <div className="ml-auto min-w-[7rem]">
            <p className=" text-sm font-[650] tabular-nums text-ink">
              {formatCurrency(figures.collected)}
            </p>
            <p className="mt-0.5 text-2xs tabular-nums text-ink-3">
              {collectedNote(payable, collected)}
            </p>
            {/* How far this family has got, at a glance. A runtime width can
                only be an inline style — `w-[${n}%]` is a class the compiler
                never sees. Same reason `ui/ProgressBar` gives. */}
            {payable > 0 && (
              <span className="mt-1.5 block h-1 w-full overflow-hidden rounded-pill bg-surface-3">
                <span
                  className={cn(
                    'block h-full rounded-pill transition-all duration-500',
                    share >= 100 ? 'bg-ok' : share > 0 ? 'bg-warn' : 'bg-crit',
                  )}
                  style={{ width: `${share}%` }}
                />
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'balance',
      header: (
        <SortableHeader
          label="Balance"
          sort="balance"
          currentSort={filters.sort}
          currentDirection={filters.sortDirection}
          onSort={(patch) => onFiltersChange(patch)}
        />
      ),
      align: 'right',
      render: (account) => {
        const balance = Number(figuresFor(account, head).balance);

        const overdue = Number(account.overdue);

        return (
          <div className="text-right">
            <p
              className={cn(
                ' text-sm font-[650] tabular-nums',
                balance > 0 ? 'text-crit' : 'text-ink-3',
              )}
            >
              {formatCurrency(balance)}
            </p>
            {/* Only with installments: how much of the balance is already late,
                which is the part worth a phone call today. */}
            {!head && overdue > 0 && (
              <p className="text-2xs font-semibold text-crit">{formatCurrency(overdue)} overdue</p>
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Fee status',
      render: (account) => (
        <Badge tone={FEE_STATUS_TONE[account.status]}>{FEE_STATUS_LABEL[account.status]}</Badge>
      ),
    },
    {
      key: 'lastPaid',
      header: 'Last payment',
      hideOnMobile: true,
      render: (account) => (
        <div className="min-w-0">
          <p
            className={cn(
              'text-sm tabular-nums',
              account.lastPaidOn ? 'text-ink' : 'text-ink-3',
            )}
          >
            {lastPaymentNote(account)}
          </p>
          {account.lastPaidAmount && (
            <p className="mt-0.5 font-mono text-sm tabular-nums text-ink-3">
              {formatCurrency(account.lastPaidAmount)}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      sticky: 'right',
      stickyWidth: 160,
      render: (account) => {
        const owes = account.status !== 'NOT_SET' && Number(account.balance) > 0;
        /**
         * Says what is still pending: a family that has paid nothing owes a
         * payment; one that has paid part owes the balance — and the office
         * should not have to read the status badge to know which.
         */
        const recordLabel = account.status === 'PARTIAL' ? 'Record balance' : 'Record payment';

        /**
         * Everything this row can do that is not its one obvious action.
         */
        const moreActions: MenuItem[] = [
          {
            key: 'open',
            label: 'Open the account',
            description: 'Every receipt, and what each head owes',
            icon: <Receipt className="h-4 w-4" />,
            onSelect: () => onOpenAccount(account.studentUuid),
          },
          ...(canCollect && owes
            ? [
                {
                  key: 'collect',
                  label: recordLabel,
                  icon: <Wallet className="h-4 w-4" />,
                  onSelect: () => onCollect(account.studentUuid),
                },
              ]
            : []),
          ...(canManage
            ? [
                {
                  key: 'charge',
                  label: account.status === 'NOT_SET' ? 'Set the fees' : 'Edit the fees',
                  icon: <PencilLine className="h-4 w-4" />,
                  onSelect: () => onSetCharge(account.studentUuid),
                },
              ]
            : []),
          ...(canRemind && owes
            ? [
                {
                  key: 'remind',
                  label: 'Send a reminder',
                  icon: <MessageSquare className="h-4 w-4" />,
                  onSelect: () => onRemind(account),
                },
              ]
            : []),
        ];

        return (
          <div
            className="flex items-center justify-end gap-1.5 whitespace-nowrap"
            // The row itself opens the account; the controls in this cell do
            // something more specific, and must not do both.
            onClick={(event) => event.stopPropagation()}
          >
            {/*
              Only where somebody actually owes: a reminder to a family that has
              paid is the message an office spends the next morning apologising
              for. `NOT_SET` is excluded too — there is no amount to ask for.
            */}
            {canRemind && owes && (
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Send ${account.fullName}'s family a fee reminder`}
                leftIcon={<MessageSquare className="h-3.5 w-3.5" />}
                onClick={() => onRemind(account)}
                className="rounded-xl font-bold text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                Remind
              </Button>
            )}

            {/* One primary action per row, and it is the one that row needs */}
            {canCollect && owes ? (
              <Button
                variant="secondary"
                size="sm"
                isLoading={busyStudentUuid === account.studentUuid}
                aria-label={`${recordLabel} for ${account.fullName}`}
                leftIcon={<Wallet className="h-3.5 w-3.5 stroke-[2.5]" />}
                onClick={() => onCollect(account.studentUuid)}
                className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white hover:text-white-700 font-bold text-sm px-3.5 py-1.5 shadow-2xs transition-colors"
              >
                {recordLabel}
              </Button>
            ) : canManage && account.status === 'NOT_SET' ? (
              <Button
                variant="secondary"
                size="sm"
                isLoading={busyStudentUuid === account.studentUuid}
                aria-label={`Set fees for ${account.fullName}`}
                leftIcon={<PencilLine className="h-3.5 w-3.5 stroke-[2.5]" />}
                onClick={() => onSetCharge(account.studentUuid)}
                className="rounded-xl bg-indigo-600 hover:bg-indigo-700 hover:text-white text-white font-bold text-sm px-3.5 py-1.5 shadow-2xs transition-colors"
              >
                Set fees
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Receipts for ${account.fullName}`}
                leftIcon={<Receipt className="h-3.5 w-3.5" />}
                onClick={() => onOpenAccount(account.studentUuid)}
                className="rounded-xl font-bold text-sm text-slate-700  dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                Receipt
              </Button>
            )}

            <Menu
              label={`More actions for ${account.fullName}`}
              items={moreActions}
              trigger={<MoreVertical className="h-4 w-4" aria-hidden />}
              triggerClassName="h-5 w-5 rounded-xl text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
            />
          </div>
        );
      },
    },
  ];

  return (
    <section className="rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-[#111c2e] flex flex-col gap-4 p-4 sm:p-5 shadow-xs w-full max-w-full overflow-hidden">
      {/* The standings, as tabs over one table */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200 dark:border-slate-800 pb-1 sm:pb-0">
        <Tabs
          options={statusTabs}
          value={status === '' ? 'all' : status}
          onChange={(value) => onFiltersChange({ status: value === 'all' ? '' : value })}
          label="Fee standings"
          className="w-full sm:flex-1 border-b-0 px-0"
        />

        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="self-end sm:self-center shrink-0 font-bold text-xs text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
            leftIcon={<X className="h-3.5 w-3.5" />}
            onClick={() => {
              onHeadChange('');
              onFiltersChange(CLEARED_FILTERS);
            }}
          >
            Clear filters
          </Button>
        )}
      </div>

      {error && (
        <QueryError title="Could not load the fee register" error={error} onRetry={refetch} />
      )}

      {/* Which head the money columns are reporting, in words */}
      {head !== '' && (
        <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
          Payable, collected and balance below are{' '}
          <span className="font-bold text-slate-700 dark:text-slate-200">{FEE_HEAD_LABEL[head].toLowerCase()}</span> only.
          The fee status beside them is for the whole account.
        </p>
      )}

      <div className="mt-2 sm:mt-3">
        <Table
          variant="plain"
          className="rounded-2xl border border-slate-200/80 dark:border-slate-800 overflow-hidden shadow-2xs"
          columns={columns}
          rows={data?.items ?? []}
          rowKey={(account) => account.studentUuid}
          isLoading={isLoading}
          onRowClick={(account) => onOpenAccount(account.studentUuid)}
          empty={
            <EmptyState
              icon={<Users className="h-5 w-5" />}
              title={hasFilters ? 'No accounts match these filters' : 'Nobody is on the register yet'}
              description={
                hasFilters
                  ? 'Adjust the search, or clear the filters to see every account.'
                  : 'Students admitted for this session appear here with what they owe.'
              }
              action={
                hasFilters ? (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      onHeadChange('');
                      onFiltersChange(CLEARED_FILTERS);
                    }}
                  >
                    Clear filters
                  </Button>
                ) : undefined
              }
            />
          }
        />
      </div>

      {data && data.meta.total > 0 && (
        <Pagination
          hideBorder
          meta={data.meta}
          itemLabel="students"
          showPageNumbers
          limitOptions={FEE_PAGE_SIZES}
          onLimitChange={(limit) => onFiltersChange({ limit })}
          onPageChange={(page) => onFiltersChange({ page })}
        />
      )}
    </section>
  );
}
